<template>
    <div v-if="results" id="results">
        <div id="results-summary" :class="version">
            <div v-if="version === 'dating'" class="container" id="dating-container">
                <div id="dating-intro">
                    <h2>Here’s your HOO-R-U Score!</h2>
                </div>
                <ul id="dating-scores" class="zero">
                    <li id="dating-smart_shopper_score">
                        <div>
                            <h4><strong>{{ Math.round(results['smart_shopper_score']) }}%</strong> Savvy</h4>
                            <div class="bar">
                                <div :style="{'width': `${results['smart_shopper_score']}%`}"></div>
                            </div>
                        </div>
                    </li>
                    <li id="dating-empathy_score">
                        <div>
                            <h4><strong>{{ Math.round(results['empathy_score']) }}%</strong> Empathetic</h4>
                            <div class="bar">
                                <div :style="{'width': `${results['empathy_score']}%`}"></div>
                            </div>
                        </div>
                    </li>
                    <li id="dating-creativity_score">
                        <div>
                            <h4><strong>{{ Math.round(results['creativity_score']) }}%</strong> Creative</h4>
                            <div class="bar">
                                <div :style="{'width': `${results['creativity_score']}%`}"></div>
                            </div>
                        </div>
                    </li>
                    <li id="dating-leadership_score">
                        <div>
                            <h4><strong>{{ Math.round(results['leadership_score']) }}%</strong> Leader</h4>
                            <div class="bar">
                                <div :style="{'width': `${results['leadership_score']}%`}"></div>
                            </div>
                        </div>
                    </li>
                    <li id="dating-trendiness_score">
                        <div>
                            <h4><strong>{{ Math.round(results['trendiness_score']) }}%</strong> Trendy</h4>
                            <div class="bar">
                                <div :style="{'width': `${results['trendiness_score']}%`}"></div>
                            </div>
                        </div>
                    </li>
                </ul>
                <ul id="dating-scores-2" class="zero">
                    <li id="dating-smart_shopper_score-2">
                        <div>
                            <h4>{{ Math.round(results['smart_shopper_score']) }}%</h4>
                            <h5>Savvy</h5>
                        </div>
                        <p>You are more savvy than <strong>{{ Math.round(results['smart_shopper_score']) }}%</strong> of the dating candidates in your circle. This measure compares how you use your practical knowledge and common sense to make smart, informed judgments.</p>
                    </li>
                    <li id="dating-empathy_score-2">
                        <div>
                            <h4 class="number">{{ Math.round(results['empathy_score']) }}%</h4>
                            <h5>Empathetic</h5>
                        </div>
                        <p>You are more empathetic than <strong>{{ Math.round(results['empathy_score']) }}%</strong> of the dating candidates in your circle. This measure compares how mindful you are of the concerns, interests and viewpoints of others. It’s indicative of how attentive you are to details and how conscientious and fun you are to be with.</p>
                    </li>
                    <li id="dating-creativity_score-2">
                        <div>
                            <h4 class="number">{{ Math.round(results['creativity_score']) }}%</h4>
                            <h5>Creative</h5>
                        </div>
                        <p>You are more creative than <strong>{{ Math.round(results['creativity_score']) }}%</strong> of the dating candidates in your circle. This measure compares how you use your innate curiosity to come up with imaginative solutions to life’s small or big problems.</p>
                    </li>
                    <li id="dating-leadership_score-2">
                        <div>
                            <h4 class="number">{{ Math.round(results['leadership_score']) }}%</h4>
                            <h5>Leader</h5>
                        </div>
                        <p>You exhibit <strong>{{ Math.round(results['leadership_score']) }}%</strong> more natural leadership qualities than other dating candidates in your circle. This measures how likely others are to pay attention and follow your lead. It shows how eager you are to get ahead while also showing others how to do so.</p>
                    </li>
                    <li id="dating-trendiness_score-2">
                        <div>
                            <h4 class="number">{{ Math.round(results['trendiness_score']) }}%</h4>
                            <h5>Trendy</h5>
                        </div>
                        <p>You are more fashionable and modern than <strong>{{ Math.round(results['trendiness_score']) }}%</strong> of the dating candidates in your circle. This measures how interested you are in trends or setting trends and how likely you are to share your sense of style with others.</p>
                    </li>
                </ul>
            </div>
            <div v-else class="container">
                <div id="intro">
                    <h2>Here's your HOO-R-U Score™ and how you rank on social media *</h2>
                </div>
                <ul id="scores" class="zero">
                    <li id="empathy_score">
                        <div class="icon"><img :src="image_url('empathy_score')" alt="" /></div>
                        <div>
                            <div class="number">{{ Math.round(results['empathy_score']) }}%</div>
                            <div class="bar">
                                <div :style="{'width': `${results['empathy_score']}%`}"></div>
                            </div>
                            <h4>Empathetic / Sharing:</h4>
                            <p>Mindful of other social media members’ concerns, interests in viewpoints. Attentive to details, conscientious and fun to be with.</p>
                        </div>
                    </li>
                    <li id="leadership_score">
                        <div class="icon"><img :src="image_url('leadership_score')" alt="" /></div>
                        <div>
                            <div class="number">{{ Math.round(results['leadership_score']) }}%</div>
                            <div class="bar">
                                <div :style="{'width': `${results['leadership_score']}%`}"></div>
                            </div>
                            <h4>Leader / Influencer:</h4>
                            <p>Exhibiting natural leadership qualities that make others listen and pay attention to. Eager to get ahead while also showing others how to do so.</p>
                        </div>
                    </li>
                    <li id="creativity_score">
                        <div class="icon"><img :src="image_url('creativity_score')" alt="" /></div>
                        <div>
                            <div class="number">{{ Math.round(results['creativity_score']) }}%</div>
                            <div class="bar">
                                <div :style="{'width': `${results['creativity_score']}%`}"></div>
                            </div>
                            <h4>Creative / Original:</h4>
                            <p>Using innate curiosity to come up with imaginative solutions to life’s small or big problems.</p>
                        </div>
                    </li>
                    <li id="smart_shopper_score">
                        <div class="icon"><img :src="image_url('smart_shopper_score')" alt="" /></div>
                        <div>
                            <div class="number">{{ Math.round(results['smart_shopper_score']) }}%</div>
                            <div class="bar">
                                <div :style="{'width': `${results['smart_shopper_score']}%`}"></div>
                            </div>
                            <h4>Savvy:</h4>
                            <p>Brand loyal yet shrewd deal hunter and excited to try new products.</p>
                        </div>
                    </li>
                    <li id="trendiness_score">
                        <div class="icon"><img :src="image_url('trendiness_score')" alt="" /></div>
                        <div>
                            <div class="number">{{ Math.round(results['trendiness_score']) }}%</div>
                            <div class="bar">
                                <div :style="{'width': `${results['trendiness_score']}%`}"></div>
                            </div>
                            <h4>Trendy:</h4>
                            <p>Interested in new trends and eager to talk about that with others.</p>
                        </div>
                    </li>
                </ul>
                <div class="asterisk-text">
                    <p><strong>* Your HOO-R-U Score sums up how well you rank among your peer group (0 to 100%).</strong> It captures the standing of your social media identity relative to members of your peer group. For example, a score of 89 means your score is higher than 89 percent of your peers or, to put it differently, you are in the top 11 percent of your peers.</p>
                </div>
            </div>
        </div>
        <div v-if="public_token" id="share-your-results" class="hide-for-print">
            <div class="important-section">
                <div class="container actions-buttons-container">
                    <h2>Would your friends agree? Share and find out!</h2>
                    <ul class="actions-buttons">
                        <li>
                            <a :href="`https://www.facebook.com/sharer.php?u=${shared_link('facebook')}`" @click="share" data-provider="facebook" data-window="width=640,height=320" class="icon facebook" title="Facebook">Facebook</a>
                        </li><li>
                            <a :href="`https://twitter.com/home?status=My HOO-R-U™ Score: ${shared_link('twitter')}`" @click="share" data-provider="twitter" data-window="width=640,height=253" class="icon twitter" title="Twitter">Twitter</a>
                        </li><li>
                            <a :href="`https://www.linkedin.com/shareArticle?url=${shared_link('linkedin')}`" @click="share" data-provider="linkedin" data-window="width=640,height=320" class="icon linkedin" title="LinkedIn">LinkedIn</a>
                        </li><li>
                            <a :href="`https://www.pinterest.com/pin/create/button/?url=${shared_link('pinterest')}`" data-window="width=800,height=400" @click="share" data-provider="pinterest" class="icon pinterest" title="Pinterest">Pinterest</a>
                        </li><li>
                            <a :href="`mailto:?subject=My HOO-R-U Score&body=${location_url}&utm_source=email`" @click="email_share" class="icon email" title="Share your results via email">Share your results via email</a>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="container important-text">
                <p><span>4 ways to save your HOO-R-U™ results.</span></p>
                <p>You can <strong>PRINT</strong> this page, <strong>EMAIL</strong> the page, <strong>SAVE</strong> the URL link or take a <strong>SNAPSHOT</strong> of these results.</p>
                <p>Come back and see how your scores change and better align with your goals over time.</p>
            </div>
            <div class="container actions-buttons-container">
                <ul id="share-actions" class="actions-buttons">
                    <li>
                        <button type="button" @click="print" class="icon print" title="Print your report">Print your report</button>
                    </li><li v-if="email_token_editable">
                        <button type="button" @click="toggle_details('email', 'email_address')" class="icon email" title="Email your report">Email your report</button>
                    </li><li>
                        <button type="button" @click="copy_link" class="icon link" title="Copy link">Copy link</button>
                    </li>
                </ul>
            </div>
            <div id="details" v-if="details">
                <Loader v-if="update_in_progress"></Loader>
                <div class="container" v-else-if="details === 'email'">
                    <form @submit="send_email" class="email-form">
                        <div><input type="email" placeholder="Your email" id="email_address" v-model="email" required="required" /></div>
                        <div><button>Send</button></div>
                    </form>
                </div>
            </div>
        </div>
        <div v-else id="try-hooru" class="important-section">
            <div>
                <ul class="container actions-buttons">
                    <li>
                        <button type="button" @click="start_process">HOO-R-U? FIND OUT</button>
                        <p><a href="https://hoo-r-u.com/">What is HOO-R-U</a></p>
                    </li>
                </ul>
            </div>
        </div>
    </div>
    <div v-else>Results not found</div>
</template>

<script>
  import Helpers from "../utils/Helpers";
  import Ajax from "../utils/Ajax";
  import Loader from "./Loader";

  const IMAGES = {
    'creativity_score': '/img/results/results-creative-original.png',
    'empathy_score': '/img/results/results-empathetic-sharing.png',
    'leadership_score': '/img/results/results-leader-influencer.png',
    'smart_shopper_score': '/img/results/results-smart-shopper.png',
    'trendiness_score': '/img/results/results-trendsetter.png',
  };

  const IMAGES_FEATURED = {
    'creativity_score': '/img/results/results-creative-original-full.png',
    'empathy_score': '/img/results/results-empathetic-sharing-full.png',
    'leadership_score': '/img/results/results-leader-influencer-full.png',
    'smart_shopper_score': '/img/results/results-smart-shopper-full.png',
    'trendiness_score': '/img/results/results-trendsetter-full.png',
  };

  const LINKS = {
    'creativity_score': 'https://hoo-r-u.com/results-creative-original/',
    'empathy_score': 'https://hoo-r-u.com/results-empathetic-sharing/',
    'leadership_score': 'https://hoo-r-u.com/results-leader-influencer/',
    'smart_shopper_score': 'https://hoo-r-u.com/results-smart-shopper/',
    'trendiness_score': 'https://hoo-r-u.com/results-trendsetter/',
  };

  export default {
    name: 'Results',
    components: {Loader},
    props: [ 'results', 'email_token', 'badge_token', 'public_token', 'access_token', 'version' ],
    data: function() {
      return {
        'details': null,
        'email': '',
        'update_in_progress': false,
        'email_token_editable': this.$props.email_token,
        'pop_state': this.change_pop_state.bind(this),
        'location_url': document.location.toString().replace('&utm_source=results', ''),
        'max_param': this.get_max_param(),
      };
    },
    mounted: function() {
      setTimeout(() => {
        document.getElementById('scores') && document.getElementById('scores').classList.remove('zero');
        document.getElementById('dating-scores') && document.getElementById('dating-scores').classList.remove('zero');
        document.getElementById('dating-scores-2') && document.getElementById('dating-scores-2').classList.remove('zero');
      }, 10);
      window.addEventListener('popstate', this.$data.pop_state);
    },
    beforeDestroy: function () {
      window.removeEventListener('popstate', this.$data.pop_state);
    },
    methods: {
      'print': function () {
        this.$parent.$data.print_mode = true;
        setTimeout(() => {
          window.print();
          this.$parent.$data.print_mode = false;
        }, 1);
      },
      'share': function (e) {
        e.preventDefault();
        window.gtag && window.gtag('event', 'questionnaire-action', {
          'event_category': 'results-share',
          'event_label': e.target.getAttribute('data-provider')
        });
        window.open(e.target.getAttribute('href'), 'share', e.target.getAttribute('data-window'));
      },
      'shared_link': function (source) {
        return encodeURIComponent(`${LINKS[this.$data.max_param]}?utm_source=${source}`);
      },
      'image_url': function (param) {
        return param === this.$data.max_param ? IMAGES_FEATURED[param] : IMAGES[param];
      },
      'get_max_param': function () {
        let max = -1;
        let max_param = null;
        for (let param in LINKS){
          if (this.$props.results[param] > max){
            max = this.$props.results[param];
            max_param = param;
          }
        }
        return max_param;
      },
      'change_pop_state': function (e) {
        if (!e.state){
          return;
        }
        if (e.state['step']){
          e.preventDefault();
          this.$parent.$data.step = e.state['step'];
        }
      },
      'copy_link': function (e) {
        e.preventDefault();
        let input;
        input = document.createElement('textarea');
        input.value = this.$data.location_url;
        input.style.margin = '-10000px 0 0 0';
        document.getElementsByTagName('body')[0].appendChild(input);
        input.select();
        document.execCommand('copy');
        input.setSelectionRange(0, 0);
        document.getElementsByTagName('body')[0].removeChild(input);
        alert('Link was copied');
      },
      'send_email': function (e) {
        e.preventDefault();
        this.$data.update_in_progress = true;
        const email_token = this.$data.email_token_editable;
        this.$data.email_token_editable = null;
        const url = `${this.API_BASE}/email-results/${email_token}`;
        Ajax.ajax('POST', url, {
          "email": this.$data.email,
        }).then(() => {
          this.$data.update_in_progress = false;
          localStorage.removeItem('email_token');
          this.$data.details = null;
          window.gtag && window.gtag('event', 'questionnaire-action', {
            'event_category': 'results-share',
            'event_label': 'email'
          });
          alert('Your results were emailed');
        }).catch(() => {
          this.$data.update_in_progress = false;
          this.$data.email_token_editable = email_token;
          alert('Something went wrong');
        });
      },
      'toggle_details': function (code, focus) {
        this.$data.details = this.$data.details === code ? null : code;
        setTimeout(() => {
          if (focus){
            document.getElementById(focus).focus();
          }
          window.scrollTo(0, document.getElementById('share-actions').offsetTop - 32 - document.getElementsByTagName('header')[0].clientHeight);
        }, 50);
      },
      'start_process': function () {
        this.$parent.$data.results = null;
        const so = Helpers.get_search_string();
        history.replaceState({'step': 'results'}, document.getElementsByTagName('title')[0].innerHTML, window.location.search);
        history.pushState({'step': 'survey'}, 'HOO-R-U', `/?utm_source=${so['utm_source'] ? so['utm_source'] : 'results'}`);
        this.$parent.$data.step = 'survey';
      },
      'email_share': function (e) {
        e && e.preventDefault();
        const subject = 'My HOO-R-U Score'
        let body = 'I just tried this free tool called HOO-R-U which told me "who I am" online.';
        body += '\n\n';
        body += 'Do you think this is me on social media? Here are my results:';
        body += '\n';
        body += `${this.$data.location_url}&utm_source=email`
        body += '\n\n';
        body += 'Follow the link to try it for yourself: https://hoo-r-u.com';
        body += '\n\n';
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`)
      },
    }
  }
</script>

<style scoped>
    a {
        color: #8fbd52;
    }
    #results {
        padding: 0;
        background: #ffffff;
    }
    #results-summary {
        background: #e3e3e3;
        padding-top: 3rem;
        padding-bottom: 1.5rem;
    }
    #results-summary.dating {
        background: #f0f0f0;
    }
    .asterisk-text {
        margin-top: 1.5rem;
        line-height: 1.5rem;
    }
    .container {
        padding: 0 3rem;
        max-width: 1200px;
        margin: 0 auto;
    }
    button {
        width: 12rem;
        padding: 0 1rem;
        font-size: 1.2rem;
        height: 3rem;
    }
    span.info {
        cursor: pointer;
        border-bottom: 1px dashed;
    }
    h2 {
        text-align: center;
        font-size: 2rem;
        font-weight: bold;
    }
    h3 {
        font-size: 1.6rem;
    }
    p {
        font-size: 1rem;
    }
    p.description {
        color: #8a8a8a;
    }
    #intro {
        text-align: center;
    }
    #scores {
        padding: 2rem 0;
        display: flex;
        margin-top: 13%;
        background: #f0f0f0;
    }
    #scores > li {
        width: 20%;
        padding: 1rem 1.5rem;
        box-sizing: border-box;
        border-left: 1px solid #dddddd;
    }
    #scores > li:first-child {
        border-left: none;
    }
    #scores > li div.number {
        font-size: 2.5rem;
        line-height: 2.5rem;
    }
    #scores > li h4 {
        font-weight: bold;
        font-size: 1.1rem;
        margin: 1rem 0 0.5rem 0;
    }
    #scores > li .icon > img {
        width: 80%;
        max-width: 200px;
        margin-top: -100%;
        display: block;
        margin-bottom: 0.5rem;
    }
    #scores > li .bar {
        height: 0.75rem;
        background: #ffffff;
        margin: 0.5rem 0;
    }
    #scores > li .bar > div {
        height: 100%;
        transition: width ease-out 0.5s;
        background: #000000;
    }
    #scores.zero > li .bar > div {
        width: 0 !important;
    }
    #scores > li p {
        line-height: 1.5rem;
    }
    #scores #empathy_score .number {
        color: #edb346;
    }
    #scores #empathy_score .bar > * {
        background: #edb346;
    }
    #scores #leadership_score .number {
        color: #231f20;
    }
    #scores #leadership_score .bar > * {
        background: #231f20;
    }
    #scores #creativity_score .number {
        color: #8fbd52;
    }
    #scores #creativity_score .bar > * {
        background: #8fbd52;
    }
    #scores #smart_shopper_score .number {
        color: #e37d4e;
    }
    #scores #smart_shopper_score .bar > * {
        background: #e37d4e;
    }
    #scores #trendiness_score .number {
        color: #32aa3c;
    }
    #scores #trendiness_score .bar > * {
        background: #32aa3c;
    }
    div.actions-buttons-container {
        text-align: center;
    }
    .actions-buttons p {
        margin-top: 0.5rem;
    }
    ul.actions-buttons {
        text-align: center;
        margin-top: -1rem;
    }
    ul.actions-buttons li {
        display: inline-block;
        vertical-align: top;
        margin-left: 1rem;
        margin-top: 1rem;
    }
    ul.actions-buttons li:first-child {
        margin-left: 0;
    }
    ul.actions-buttons li p {
    }
    ul.actions-buttons li > *.icon {
        display: block;
        width: 3rem;
        height: 3rem;
        background-image: url("./../assets/results/socials-sprite.png");
        background-repeat: no-repeat;
        background-size: 384px 384px;
        overflow: hidden;
        text-indent: -10000px;
    }
    ul.actions-buttons li > *.icon.facebook {
        background-position: 0 0;
    }
    ul.actions-buttons li > *.icon.twitter {
        background-position: -96px -96px;
    }
    ul.actions-buttons li > *.icon.linkedin {
        background-position: -48px -48px;
    }
    ul.actions-buttons li > *.icon.pinterest {
        background-position: -240px -240px;
    }
    ul.actions-buttons li > *.icon.print {
        background-position: -144px -144px;
    }
    ul.actions-buttons li > *.icon.email {
        background-position: -192px -192px;
    }
    ul.actions-buttons li > *.icon.xing {
        background-position: -288px -288px;
    }
    ul.actions-buttons li > *.icon.link {
        background-position: -336px -336px;
    }
    .important-section {
        background: #000000;
        color: #ffffff;
        padding: 3rem 0;
    }
    .important-section h2 {
        margin-bottom: 1.5rem;
    }
    .important-text {
        text-align: center;
    }
    .important-text p > span {
        color: #ea9502;
        font-weight: bold;
        font-size: 2rem;
    }
    .important-text p {
        font-size: 1.3rem;
        line-height: 2rem;
        margin-bottom: 0.5rem;
    }
    #details {
        border-top: 1px solid #dddddd;
        padding-top: 2rem;
        padding-bottom: 2rem;
        background: #ffffff;
        margin-bottom: 0!important;
    }
    #details .row {
        display: flex;
        margin-bottom: 1.5rem;
    }
    #details .row:last-child {
        margin-bottom: 0;
    }
    #details .row > * {
        width: 100%;
        margin: 0 2rem;
    }
    #details > div.container > h3 {
        margin-bottom: 1rem;
    }
    #details .row > * > * {
        margin-top: 1rem;
    }
    #details .row > * > *:first-child {
        margin-top: 0;
    }
    #details .row > *:first-child {
        margin-left: 0;
    }
    #details .row > *:last-child {
        margin-left: 0;
    }
    #details .media > img,
    #details .media > iframe {
        display: block;
        width: 100%;
    }
    #details .media p {
        text-align: center;
        margin-top: 0.5rem;
    }
    #details .loader-wrapper {
        top: 0;
        left: 0;
        position: static;
        margin: 0 auto;
    }
    #share-your-results {
        padding-bottom: 3rem;
    }
    #share-your-results > * {
        margin-bottom: 3rem;
    }
    #share-your-results > *:last-child {
        margin-bottom: 0;
    }
    #share-your-results > .important-section {
        margin-bottom: 1.5rem;
    }
    #share-your-results > .important-section ul.actions-buttons li {
        margin-left: 0.5rem;
    }
    #share-your-results > .important-section ul.actions-buttons li:first-child {
        margin-left: 0;
    }
    #share-your-results > .important-section ul.actions-buttons li > *.icon {
        width: 4rem;
        height: 4rem;
        background-size: 32rem 32rem;
    }
    #share-your-results > .important-section ul.actions-buttons li > *.icon.facebook {
        background-position: 0 0;
    }
    #share-your-results > .important-section ul.actions-buttons li > *.icon.twitter {
        background-position: -8rem -8rem;
    }
    #share-your-results > .important-section ul.actions-buttons li > *.icon.linkedin {
        background-position: -4rem -4rem;
    }
    #share-your-results > .important-section ul.actions-buttons li > *.icon.pinterest {
        background-position: -20rem -20rem;
    }
    #share-your-results > .important-section ul.actions-buttons li > *.icon.email {
        background-color: transparent;
        background-position: -24rem -24rem;
    }
    .email-form {
        display: flex;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;
    }
    .email-form > div:first-child {
        width: 100%;
    }
    .email-form input {
        width: 100%;
        background: #ffffff;
        outline: none;
        padding: 0.5rem 1rem;
        height: 3rem;
        line-height: 1.5rem;
        font-size: 1.3rem;
        box-sizing: border-box;
        border: 1px solid #dddddd;
        border-right: none;
    }
    #dating-container {
        max-width: 1000px;
    }
    #dating-intro > h2 {
        font-size: 2.7rem;
        line-height: 2.7rem;
    }
    #dating-scores {
        margin: 3rem 8rem;
    }
    #dating-scores-2 > li {
        display: flex;
        padding-bottom: 1.5rem;
        margin-bottom: 1.5rem;
        border-bottom: 1px solid #dddddd;
    }
    #dating-scores-2 > li:last-child {
        padding-bottom: 0;
        margin-bottom: 0;
        border-bottom: none;
    }
    #dating-scores-2 > li > div {
        min-width: 8rem;
        max-width: 8rem;
        text-align: center;
    }
    #dating-scores-2 > li > div > h4 {
        font-size: 2rem;
        font-weight: bold;
    }
    #dating-scores-2 > li > div > h5 {
        text-transform: uppercase;
        font-weight: bold;
    }
    #dating-scores-2 > li > p {
        font-size: 1.1rem;
    }
    #dating-smart_shopper_score-2 > div {
        color: #edb346;
    }
    #dating-empathy_score-2 > div {
        color: #dd2b22;
    }
    #dating-creativity_score-2 > div {
        color: #8fbd52;
    }
    #dating-leadership_score-2 > div {
        color: #e37d4e;
    }
    #dating-trendiness_score-2 > div {
        color: #32aa3c;
    }
    #dating-scores .bar {
        height: 1.25rem;
        background: #ffffff;
        margin: 0.25rem 0 1.25rem 0;
    }
    #dating-scores .bar div {
        height: 100%;
        transition: width 1000ms cubic-bezier(0.785, 0.135, 0.15, 0.86);
    }
    #dating-scores > li:nth-child(1) .bar > div {
        transition-delay: 0s;
    }
    #dating-scores > li:nth-child(2) .bar > div {
        transition-delay: 0.25s;
    }
    #dating-scores > li:nth-child(3) .bar > div {
        transition-delay: 0.50s;
    }
    #dating-scores > li:nth-child(4) .bar > div {
        transition-delay: 0.75s;
    }
    #dating-scores > li:nth-child(5) .bar > div {
        transition-delay: 1s;
    }
    #dating-scores.zero .bar div {
        width: 0 !important;
    }
    #dating-smart_shopper_score .bar div {
        background: #edb346;
    }
    #dating-empathy_score .bar div {
        background: #dd2b22;
    }
    #dating-creativity_score .bar div {
        background: #8fbd52;
    }
    #dating-leadership_score .bar div {
        background: #e37d4e;
    }
    #dating-trendiness_score .bar div {
        background: #32aa3c;
    }
    @media only screen and (max-width: 640px) {
        .email-form button {
            width: auto;
        }
        #results-summary {
            padding-top: 1rem;
        }
        .container {
            padding: 0 1.5rem;
        }
        ul.actions-buttons {
            margin-left: -1.25rem;
            margin-right: -1.25rem;
        }
        ul.actions-buttons li {
            margin-left: 0.5rem;
        }
    }
    @media only screen and (max-width: 800px) {
        #details .row {
            display: block;
        }
        #details .row > * {
            margin-bottom: 1.5rem;
        }
        #dating-scores {
            margin-left: 0;
            margin-right: 0;
        }

    }
    @media only screen and (max-width: 1024px) {
        #scores {
            padding: 2rem;
            margin-top: 1rem;
            display: block;
        }
        #scores > li {
            width: 100%;
            padding: 1rem 0;
            box-sizing: border-box;
            border-left: none;
            border-top: 1px solid #dddddd;
            display: flex;
        }
        #scores > li:first-child {
            border-top: none;
        }
        #scores > li > div:first-child {
            margin-right: 2rem;
            max-width: 200px;
            width: 25%;
        }
        #scores > li > div:last-child {
            width: 75%;
        }
        #scores > li .icon img {
            width: 100%;
            margin: -2rem 0 0 0;
        }
    }
    @media only screen and (max-width: 480px) {
        #scores > li {
            display: block;
        }
        #dating-scores-2 > li {
            display: block;
        }
        #dating-scores-2 > li > div {
            margin: 0 auto 0.5rem;
        }
        #scores > li > div:first-child {
            margin-right: 0;
            max-width: 200px;
            margin-bottom: 1rem;
            width: auto;
        }
        #scores > li > div:last-child {
            width: auto;
        }
    }
</style>